import {
  PaymentMethodCreateParams,
  Stripe,
  StripeElements,
} from "@stripe/stripe-js";
import { i18n } from "next-i18next";

import { PaymentResult } from "../types";
import { SelectablePaymentMethodEnum } from "config/site";

const loadError = () => ({
  message: i18n?.t("add_card.error.load"),
});

const requestError = () => ({
  message: i18n?.t("add_card.error.request"),
});

const setupStripe = async ({
  stripe,
  elements,
  clientSecret,
  billingDetails,
  isPayout,
}: {
  stripe: Stripe | null;
  elements: StripeElements | null;
  clientSecret: string;
  billingDetails: PaymentMethodCreateParams.BillingDetails;
  isPayout?: boolean;
}): Promise<PaymentResult> => {
  if (!stripe || !elements) {
    return { error: loadError() };
  }

  if (!clientSecret) {
    return { error: loadError() };
  }

  const { error: submitError } = await elements.submit();

  if (submitError) {
    return submitError.message
      ? {
          error: { message: submitError.message },
        }
      : { error: requestError() };
  }

  try {
    // Use the clientSecret and Elements instance to confirm the setup
    const { error } = isPayout
      ? await stripe.confirmPayPalSetup(clientSecret, {
          return_url: window.location.href.split("?")[0],
          mandate_data: {
            customer_acceptance: {
              type: "online",
              online: {
                infer_from_client: true,
              },
            },
          },
        })
      : await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            payment_method_data: {
              billing_details: billingDetails,
            },
            mandate_data: {
              customer_acceptance: {
                type: "online",
                online: {
                  infer_from_client: true,
                },
              },
            },
            return_url: window.location.href.split("?")[0],
          },

          // Uncomment below if you only want redirect for redirect-based payments
          // redirect: "if_required",
        });

    if (error) {
      return error.message
        ? {
            error: { message: error.message },
          }
        : { error: requestError() };
    }

    return {
      error: null,
      paymentMethod: SelectablePaymentMethodEnum.Stripe,
    };
  } catch (e) {
    return { error: requestError() };
  }
};

export default setupStripe;
